<template>
  <div class="send-notification">
    <div class="page-header">
      <h1>Send Notification</h1>
      <p class="subtitle">Send notifications to selected users</p>
    </div>

    <div class="notification-form-container">
      <form @submit.prevent="handleSendNotification" class="notification-form">
        <!-- User Selection -->
        <div class="form-group">
          <label class="form-label required">Recipients</label>
          <div class="user-selection-type">
            <label class="radio-option">
              <input type="radio" v-model="selectionType" value="single" />
              <span>Single User</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="selectionType" value="multiple" />
              <span>Multiple Users</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="selectionType" value="all" />
              <span>All Users</span>
            </label>
          </div>

          <!-- Single User Selection -->
          <div v-if="selectionType === 'single'" class="user-select-container">
            <div class="user-search">
              <input 
                type="text" 
                v-model="userSearch" 
                @input="handleUserSearchInput"
                placeholder="Search users..."
                class="form-input"
              />
              <i class="fas fa-search search-icon"></i>
            </div>
            <div v-if="loadingUsers" class="search-status">Searching users...</div>
            <select v-model="selectedUser" class="form-select" required size="10">
              <option value="">Select a user...</option>
              <option v-for="user in users" :key="user.id" :value="user.id">
                {{ getUserDisplayName(user) }} ({{ user.email }})
              </option>
            </select>
            <div v-if="!loadingUsers && users.length === 0" class="no-results">
              No users found matching "{{ userSearch }}"
            </div>
          </div>

          <!-- Multiple Users Selection -->
          <div v-if="selectionType === 'multiple'" class="user-select-container">
            <div class="user-search">
              <input 
                type="text" 
                v-model="userSearch" 
                @input="handleUserSearchInput"
                placeholder="Search users..."
                class="form-input"
              />
              <i class="fas fa-search search-icon"></i>
            </div>
            <div v-if="loadingUsers" class="search-status">Searching users...</div>
            
            <div class="user-list">
              <label 
                v-for="user in users" 
                :key="user.id" 
                class="user-checkbox"
              >
                <input 
                  type="checkbox" 
                  :value="user.id" 
                  v-model="selectedUsers"
                />
                <span class="user-info">
                  <span class="user-name">{{ getUserDisplayName(user) }}</span>
                  <span class="user-email">{{ user.email }}</span>
                </span>
              </label>
            </div>

            <div v-if="!loadingUsers && users.length === 0" class="no-results">
              No users found matching "{{ userSearch }}"
            </div>

            <div v-if="selectedUsers.length > 0" class="selected-count">
              {{ selectedUsers.length }} user(s) selected
            </div>
          </div>

          <!-- All Users Info -->
          <div v-if="selectionType === 'all'" class="all-users-info">
            <i class="fas fa-info-circle"></i>
            This notification will be sent to all users
          </div>
        </div>

        <!-- Sender Display -->
        <div class="form-group">
          <label class="form-label required">Show Sender As</label>
          <div class="user-selection-type">
            <label class="radio-option">
              <input type="radio" v-model="senderDisplay" value="adcrew" />
              <span>AdCrew</span>
            </label>
            <label class="radio-option">
              <input type="radio" v-model="senderDisplay" value="fullname" />
              <span>My Name</span>
            </label>
          </div>
        </div>

        <!-- Notification Type -->
        <div class="form-group">
          <label class="form-label required">Type</label>
          <select v-model="notificationType" class="form-select" required>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>

        <!-- Title -->
        <div class="form-group">
          <label class="form-label required">Title</label>
          <input 
            type="text" 
            v-model="title" 
            class="form-input"
            placeholder="Enter notification title"
            required
            maxlength="512"
          />
        </div>

        <!-- Message -->
        <div class="form-group">
          <label class="form-label required">Message</label>
          <textarea 
            v-model="message" 
            class="form-textarea"
            placeholder="Enter notification message"
            required
            rows="4"
          ></textarea>
        </div>

        <!-- Optional Metadata -->
        <div class="form-group">
          <label class="form-label">Route (Optional)</label>
          <input 
            type="text" 
            v-model="route" 
            class="form-input"
            placeholder="/path/to/page (optional)"
          />
          <small class="form-help">Users will be redirected to this route when clicking the notification</small>
        </div>

        <!-- Actions -->
        <div class="form-actions">
          <button 
            type="button" 
            @click="resetForm" 
            class="btn btn-secondary"
            :disabled="sending"
          >
            Reset
          </button>
          <button 
            type="submit" 
            class="btn btn-primary"
            :disabled="sending || !isFormValid"
          >
            <i v-if="sending" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-paper-plane"></i>
            {{ sending ? 'Sending...' : 'Send Notification' }}
          </button>
        </div>
      </form>

      <!-- Preview -->
      <div class="notification-preview">
        <h3>Preview</h3>
        <div class="preview-notification" :class="`type-${notificationType}`">
          <div class="preview-icon">
            <i :class="getNotificationIcon(notificationType)"></i>
          </div>
          <div class="preview-content">
            <div class="preview-title">{{ title || 'Notification Title' }}</div>
            <div class="preview-message">{{ message || 'Notification message will appear here...' }}</div>
            <div class="preview-time">Just now</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Messages -->
    <transition name="fade">
      <div v-if="successMessage" class="alert alert-success">
        <i class="fas fa-check-circle"></i>
        {{ successMessage }}
      </div>
    </transition>

    <transition name="fade">
      <div v-if="errorMessage" class="alert alert-error">
        <i class="fas fa-exclamation-circle"></i>
        {{ errorMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { RepositoryFactory } from '@/services/repositories/AuthAPI/RepositoryFactory'

const UsersRepo = RepositoryFactory.get('users')

export default {
  name: 'SendNotification',
  data() {
    return {
      selectionType: 'single',
      selectedUser: '',
      selectedUsers: [],
      userSearch: '',
      senderDisplay: 'adcrew',
      notificationType: 'info',
      title: '',
      message: '',
      route: '',
      users: [],
      sending: false,
      successMessage: '',
      errorMessage: '',
      loadingUsers: false,
      userSearchDebounce: null
    }
  },
  computed: {
    isFormValid() {
      if (!this.title || !this.message) return false
      
      if (this.selectionType === 'single' && !this.selectedUser) return false
      if (this.selectionType === 'multiple' && this.selectedUsers.length === 0) return false
      
      return true
    }
  },
  methods: {
    ...mapActions('Notifications', ['sendNotification']),

    handleUserSearchInput() {
      if (this.selectionType === 'all') {
        return
      }

      if (this.userSearchDebounce) {
        clearTimeout(this.userSearchDebounce)
      }

      this.userSearchDebounce = setTimeout(() => {
        this.searchUsers(this.userSearch)
      }, 300)
    },

    async handleSendNotification() {
      this.sending = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        const payload = {
          title: this.title,
          message: this.message,
          notification_type: this.notificationType,
          sender_display: this.senderDisplay,
          metadata: {}
        }

        if (this.route) {
          payload.metadata.route = this.route
        }

        // Determine recipients based on selection type
        if (this.selectionType === 'single') {
          payload.recipient = this.selectedUser
        } else if (this.selectionType === 'multiple') {
          payload.recipients = this.selectedUsers
        } else if (this.selectionType === 'all') {
          payload.all_users = true
        }

        await this.sendNotification(payload)
        
        this.successMessage = 'Notification sent successfully!'
        this.resetForm()
        
        setTimeout(() => {
          this.successMessage = ''
        }, 5000)
      } catch (error) {
        this.errorMessage = 'Failed to send notification: ' + (error.message || 'Unknown error')
        setTimeout(() => {
          this.errorMessage = ''
        }, 5000)
      } finally {
        this.sending = false
      }
    },
    
    resetForm() {
      this.selectionType = 'single'
      this.selectedUser = ''
      this.selectedUsers = []
      this.userSearch = ''
      this.users = []
      this.senderDisplay = 'adcrew'
      this.notificationType = 'info'
      this.title = ''
      this.message = ''
      this.route = ''
      this.searchUsers('')
    },
    
    getUserDisplayName(user) {
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`
      }
      return user.name || user.username || 'Unknown User'
    },
    
    getNotificationIcon(type) {
      const icons = {
        info: 'fas fa-info-circle',
        success: 'fas fa-check-circle',
        warning: 'fas fa-exclamation-triangle',
        error: 'fas fa-exclamation-circle'
      }
      return icons[type] || icons.info
    },
    
    async searchUsers(search = '') {
      if (this.selectionType === 'all') {
        this.users = []
        return
      }

      this.loadingUsers = true
      try {
        const response = await UsersRepo.query({
          page: 1,
          limit: 30,
          search
        })
        this.users = response.data.results || response.data
      } catch (error) {
        console.error('Error searching users:', error)
        this.errorMessage = 'Failed to search users'
        setTimeout(() => {
          this.errorMessage = ''
        }, 5000)
      } finally {
        this.loadingUsers = false
      }
    }
  },
  watch: {
    selectionType(newSelectionType) {
      this.selectedUser = ''

      if (newSelectionType !== 'multiple') {
        this.selectedUsers = []
      }

      if (newSelectionType === 'single' || newSelectionType === 'multiple') {
        this.searchUsers(this.userSearch)
      } else {
        this.users = []
      }
    }
  },
  mounted() {
    this.searchUsers('')
  },
  beforeDestroy() {
    if (this.userSearchDebounce) {
      clearTimeout(this.userSearchDebounce)
    }
  }
}
</script>

<style scoped>
.send-notification {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.notification-form-container {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
}

.notification-form {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  font-size: 14px;
}

.form-label.required::after {
  content: '*';
  color: #ef4444;
  margin-left: 4px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.form-help {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.user-selection-type {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-option input[type="radio"] {
  cursor: pointer;
}

.user-select-container {
  margin-top: 12px;
}

.form-select[size] {
  height: auto;
  padding: 4px;
}

.form-select[size] option {
  padding: 8px 12px;
  cursor: pointer;
}

.form-select[size] option:hover {
  background-color: #f8fafc;
}

.user-search {
  position: relative;
  margin-bottom: 12px;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-status {
  margin-bottom: 10px;
  color: #64748b;
  font-size: 13px;
}

.user-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px;
}

.user-checkbox {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.user-checkbox:hover {
  background-color: #f8fafc;
}

.user-checkbox input[type="checkbox"] {
  margin-right: 12px;
  cursor: pointer;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

.user-email {
  font-size: 12px;
  color: #94a3b8;
}

.selected-count {
  margin-top: 8px;
  font-size: 13px;
  color: #3b82f6;
  font-weight: 500;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.all-users-info {
  padding: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  color: #1e40af;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: #f1f5f9;
  color: #64748b;
}

.btn-secondary:hover:not(:disabled) {
  background: #e2e8f0;
}

.notification-preview {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 24px;
}

.notification-preview h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.preview-notification {
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.preview-notification.type-info {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.preview-notification.type-success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.preview-notification.type-warning {
  background: #fffbeb;
  border-color: #fde68a;
}

.preview-notification.type-error {
  background: #fef2f2;
  border-color: #fecaca;
}

.preview-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.type-info .preview-icon {
  background: #dbeafe;
  color: #3b82f6;
}

.type-success .preview-icon {
  background: #dcfce7;
  color: #22c55e;
}

.type-warning .preview-icon {
  background: #fef3c7;
  color: #f59e0b;
}

.type-error .preview-icon {
  background: #fee2e2;
  color: #ef4444;
}

.preview-content {
  flex: 1;
  min-width: 0;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.preview-message {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
  line-height: 1.4;
}

.preview-time {
  font-size: 12px;
  color: #94a3b8;
}

.alert {
  padding: 16px;
  border-radius: 6px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.alert-success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.alert-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .notification-form-container {
    grid-template-columns: 1fr;
  }
  
  .notification-preview {
    position: static;
  }
}
</style>
